import type { BatchExecution, BatchStatus } from './types';

/**
 * 배치 화면의 표시 규칙. (V51)
 *
 * 화면 컴포넌트에서 빼낸 이유
 *   "몇 시간 전인가" 와 "이 잡이 늦은 것인가" 는 규칙이지 표시가 아니다.
 *   특히 지각 판정은 틀려도 화면상으로는 그럴듯해 보여 눈으로 못 잡는다.
 */

/**
 * 잡 이름을 사람이 읽는 말로. 모르는 잡은 원래 이름을 그대로 쓴다.
 *
 * 🔴 왼쪽 키는 절대 바꾸지 않는다.
 *   키는 DB(cm_batch_execution.job_name)에 쌓인 값이다. 바꾸면 그 이전 이력과 끊긴다.
 *   화면 용어를 바꿀 때 고치는 것은 오른쪽뿐이다. 그래서 screener 키가
 *   '자동 종목 선별' 로 뜬다 — 어긋나 보이지만 의도한 것이다.
 */
const JOB_LABEL: Record<string, string> = {
  'trading.stock-master-sync': '종목 마스터',
  'trading.market-calendar-sync': '영업일 캘린더',
  'trading.dart-corp-sync': 'DART 매핑',
  'trading.journal-evaluation': '지난 추천 채점',
  'trading.daily-candle-sync': '일봉 수집',
  'trading.account-sync': '계좌·체결',
  'trading.screener': '자동 종목 선별',
  // 2026-10-09 병합 전 화면 점검(docs/ui-sweep/2026-10-09-feat-challenger.md) — 표에 없어 영어 키로 뜨던 잡
  'trading.daily-candle-sync-kosdaq': '일봉 수집(코스닥)',
  'trading.daily-candle-sync-evening': '일봉 수집(저녁)',
  'trading.daily-candle-sync-us': '미국 일봉 수집',
  'trading.market-cap-sync': '시가총액',
  'trading.account-sync-kr-close': '계좌·체결(국내 마감)',
  'trading.account-sync-us-close': '계좌·체결(미국 마감)',
  'trading.fee-watch': '실계좌 수수료 확인',
  'trading.autotrade-mode-apply': '자동매매 모드 적용',
  'trading.band-target-us': '미국 저점매수 감시대상',
  'trading.band-snapshot-us': '미국 모의투자 하루 평가',
  'trading.trade-fact-us': '미국 거래 사실표',
  // 2026-10-16 화면 점검 — 11:20 잡이 미국 스윙 · 미국 추천 회고도 쓴다(BandUsReviewJob) · 키는 그대로
  'trading.band-review-us': '미국 회고(저점매수 · 스윙 · 추천)',
  'trading.preferred-candle-selection': '우선주 차트 선정(월 1회)',
  // 2026-10-10 스윙(4회차) — 국내 07:50 · 미국 10:50 감시 표(그달 첫 거래일엔 풀도)
  'trading.swing-targets': '스윙 감시 표',
  'trading.swing-targets-us': '미국 스윙 감시 표',
  // 2026-10-10 미국 추천 R3(4회차) — 10:45 그날 밤 미국 장 후보 선별
  'trading.screener-us': '미국 추천 선별',
  // 2026-10-16 화면 점검 — 표에 없어 영어 키로 뜨던 잡(InvestorFlowDailyJob 04:30)
  'trading.investor-flow-daily': '투자자별 수급(어제 확정치)',
  'trading.investor-flow-backfill': '투자자별 수급 과거 채우기',
  // 2026-10-19 화면 점검 — 🔎 AI 2차 검토 잡 넷이 표에 없어 영어 키로 뜨던 것(관리자 › 배치 · 알림 종)
  'trading.ai-source-collect': 'AI 검토 공시 · 기사 쌓기',
  'trading.ai-stock-trend': 'AI 검토 종목 동향(공매도 · 대차 · 신용 · 프로그램)',
  'trading.ai-fred': 'AI 검토 미국 거시 지표(FRED)',
  'trading.ai-episode-close': 'AI 검토 국면 끝 판정',
  // 한글 키는 BandBuyDigestJob 이 그대로 쌓는다 — 지금은 스윙 · 미국 추천 요약도 같은 잡이 보낸다(키를 못 바꿔 이름으로 넓힌다)
  '저점매수 매수 요약': '매수 요약(저점매수 · 스윙)',
  '미국 저점매수 밤사이 매수 요약': '미국 밤사이 요약(저점매수 · 스윙 · 추천)',
  'benefit.recurring-notify': '혜택 알림',

  // 가계부 (세션 B). 접두사로 도메인이 갈린다
  'ledger.upload-reminder': '업로드 리마인더',
  'ledger.kb-benefit-reminder': 'KB 실적 알림',
  'ledger.monthly-summary': '월간 집계',
};

/**
 * 잡 이름을 한국어로 바꾼다.
 *
 * 모르는 이름은 그대로 돌려준다. 세션 B 가 가계부 배치를 붙이면
 * 이 표에 없어도 화면에는 뜬다 — 안 뜨는 것보다 낫다.
 */
export function jobLabel(jobName: string): string {
  return JOB_LABEL[jobName] ?? jobName;
}

/** 배지 톤. components/ui 의 Badge tone 과 같은 값이다. */
export function statusTone(status: BatchStatus): 'success' | 'danger' | 'neutral' {
  if (status === 'SUCCESS') {
    return 'success';
  }
  if (status === 'FAILED') {
    return 'danger';
  }
  // 🔴 SKIPPED 는 회색이다. 오류가 아니다.
  //   휴장일이거나 이미 실행 중이었다는 뜻인데, 빨간불로 칠하면
  //   연휴 아침마다 경고가 뜨고 진짜 실패가 묻힌다.
  return 'neutral';
}

export function statusLabel(status: BatchStatus): string {
  if (status === 'SUCCESS') {
    return '성공';
  }
  if (status === 'FAILED') {
    return '실패';
  }
  return '건너뜀';
}

/**
 * 소요 시간을 읽기 쉽게.
 *
 * 일봉 수집이 40분쯤 걸린다. 밀리초로 두면 2400000 을 사람이 세게 된다.
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}초`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}분` : `${minutes}분 ${rest}초`;
}

/**
 * 마지막 실행이 얼마나 지났는지 — lib/format.ts 로 옮겼다(2026-10-16 · 계좌 화면 "마지막 동기화" 가 두 번째로 쓴다).
 * 이 화면과 테스트가 부르던 이름을 그대로 둔다.
 */
export { formatSince } from '@/lib/format';

/**
 * 이 잡이 너무 오래 안 돌았는가.
 *
 * 🔴 왜 필요한가
 *   실패는 빨간 배지로 보이지만, **아예 안 돈 것은 아무 표시도 없다.**
 *   스케줄러가 죽거나 컨테이너가 재시작 루프에 빠지면 새 행이 안 생기고,
 *   화면에는 어제 성공한 초록 배지가 그대로 남는다.
 *   2026-08-15 에 backend 가 크래시 루프에 빠졌을 때가 정확히 그 상황이었다.
 *
 * 기준을 36시간으로 잡은 이유
 *   가장 드물게 도는 잡도 하루 한 번이다. 24시간으로 잡으면 배치가 조금만
 *   늦어도 경고가 뜨고, 48시간으로 잡으면 하루를 통째로 놓친다.
 *   36시간이면 "하루를 건너뛰었다" 를 확실히 잡으면서 오탐이 없다.
 *
 * 평일에만 도는 잡(일봉·계좌·자동 종목 선별)은 주말에 자연히 이 값을 넘는다.
 * 그래서 SKIPPED 행도 최근 실행으로 센다 — 휴장일에도 행은 남기 때문이다.
 */
export const STALE_THRESHOLD_HOURS = 36;

export function isStale(execution: BatchExecution, now: Date = new Date()): boolean {
  const elapsedHours =
    (now.getTime() - new Date(execution.startedAt).getTime()) / 3_600_000;
  return elapsedHours > STALE_THRESHOLD_HOURS;
}
